/* Copyright (c) 2020-present CloudIO Inc */

import { getName } from '../../../registry/Manager';
import { Platform } from '../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../../registry/Component.type';
import { ItemType } from '../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../registry/Components';
import { defaultValue } from './CardHeader';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { typographyVariant } from 'componentregistry/registry/constants';

const name = 'Card Header';

const CardHeaderManager: ComponentManagerFn<'CardHeader'> = (
  platform: Platform,
): ComponentManager<'CardHeader'> => {
  return {
    ...baseComponentManager<'CardHeader'>(platform, 'CardHeader'),
    defaultChildren: ['Avatar', 'IconButton'],
    onChildMoveIn: ({ page, value }) => {
      // Avatar must be the first child
      value.children = value.children.sort((a, b) => {
        const aItem = page[a];
        const bItem = page[b];
        if (aItem && bItem) {
          if (aItem.type === 'Avatar' && bItem.type !== 'Avatar') {
            return -1;
          }
          if (bItem.type === 'Avatar' && aItem.type !== 'Avatar') {
            return 1;
          }
        }
        return 0;
      });
    },
    isChildAllowed: (page, item, childType, { pageId, appUid }) => {
      if (
        !(
          [
            'ActionButton',
            'Avatar',
            'Badge',
            'Button',
            'ButtonDropdown',
            'ButtonGroup',
            'IconButton',
            'ProgressIndicator',
            'Toolbar',
            'GridPersonalizationMenu',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      if (childType === 'Avatar') {
        const children = platform.getChildrenOfType(
          appUid,
          pageId,
          page,
          item,
          childType,
        );
        return children.length === 0
          ? true
          : `${getName(childType)} already added to ${name}`;
      }
      return true;
    },
    defaultValue,
    props: {
      title: {
        label: 'Title',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Header',
      },
      titleVariant: {
        label: 'Title Variant',
        type: 'select',
        options: typographyVariant,
        category: 'Header',
      },
      titleFontSize: {
        label: 'Title Font Size',
        type: 'string',
        category: 'Header',
      },
      titleFontWeight: {
        label: 'Title Font Weigth',
        type: 'number',
        category: 'Header',
        min: 100,
        max: 900,
        step: 100,
      },
      titleFontFamily: {
        label: 'Title Font Family',
        type: 'string',
        category: 'Header',
      },
      titleFontStyle: {
        label: 'Title Font Style',
        type: 'select',
        options: toOptions(['italic', 'normal', 'oblique']),
        category: 'Header',
      },
      titleNoWrap: {
        label: 'Title No Wrap',
        type: 'boolean',
        helpText:
          'If checked, the text will not wrap, but instead will truncate with a text overflow ellipsis.',
        category: 'Header',
      },
      titleColor: {
        label: 'Title Color',
        type: 'ColorExpression',
        category: 'Header',
      },
      titleParagraph: {
        label: 'Title Paragraph',
        type: 'boolean',
        helpText: 'If checked, the text will have a bottom margin.',
        category: 'Header',
      },
      subheader: {
        label: 'Sub Title',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Sub Header',
      },
      subheaderVariant: {
        label: 'Subheader Variant',
        type: 'select',
        options: typographyVariant,
        category: 'Sub Header',
      },
      subheaderFontSize: {
        label: 'Subheader Font Size',
        type: 'string',
        category: 'Sub Header',
      },
      subheaderFontWeight: {
        label: 'Subheader Font Weigth',
        type: 'number',
        category: 'Sub Header',
        min: 100,
        max: 900,
        step: 100,
      },
      subheaderFontFamily: {
        label: 'Subheader Font Family',
        type: 'string',
        category: 'Sub Header',
      },
      subheaderFontStyle: {
        label: 'Subheader Font Style',
        type: 'select',
        options: toOptions(['italic', 'normal', 'oblique']),
        category: 'Sub Header',
      },
      subheaderNoWrap: {
        label: 'Subheader No Wrap',
        type: 'boolean',
        helpText:
          'If checked, the text will not wrap, but instead will truncate with a text overflow ellipsis.',
        category: 'Sub Header',
      },
      subheaderColor: {
        label: 'Subheader Color',
        type: 'ColorExpression',
        category: 'Sub Header',
      },
      subheaderParagraph: {
        label: 'Subheader Paragraph',
        type: 'boolean',
        helpText: 'If checked, the text will have a bottom margin.',
        category: 'Sub Header',
      },
      bgcolor: {
        label: 'Background Color',
        type: 'ColorExpression',
        helpText: 'e.g. divider or primary.light or any valid CSS color string',
        category: 'Look & Feel',
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        category: 'Look & Feel',
      },
      border: {
        label: 'Border',
        type: 'border',
        category: 'Look & Feel',
        helpText:
          'Select here to apply the borders.This border size will be included in height/width',
      },
    },
    getDisplayText: (item) => item.props.title.value,
    name,
    canHaveChildren: () => true,
    category: 'Containers',
    showEmptyPlaceholderInDesignerView: false,
    icon: { iconName: 'io-card-header', iconType: 'cloudio' },
    description:
      'Use this component to add header text, icon and background color to the Card. You can also add buttons/trigger to this component.',
  };
};

export default CardHeaderManager;
